import React from 'react'
import type { Appointment, OpeningWindow, TimeBlock, TimeOff } from '../../api/client'
import { useApp } from '../../context/AppContext'
import { blockDefaultEnd, dayPlan } from '../../calendar/dayRows'
import AppointmentRow from '../../components/AppointmentRow'
import { formatDuration, formatPrice, minutesToTime } from '../../catalog/format'
import { Icon } from '../../ui/Icon'
import { useCardAccount } from '../../payments/useCardAccount'
import { canTakeCardPayments } from '../../payments/visibility'
import { useNow } from '../../calendar/useNow'

interface DayViewProps {
  date: string
  appointments: Appointment[]
  windows: OpeningWindow[]
  timeOff: TimeOff[]
  timeBlocks: TimeBlock[]
}

export default function DayView({ date, appointments, windows, timeOff, timeBlocks }: DayViewProps) {
  const { dispatch } = useApp()
  const plan = dayPlan(date, windows, timeOff, appointments, timeBlocks)
  const cardShown = canTakeCardPayments(useCardAccount())
  // Today, a "Now" line sits before the first row that hasn't started yet.
  const now = useNow()
  const nowAt = now.date === date && plan.rows.length > 0 ? plan.rows.findIndex((r) => r.start > now.minute) : -2
  const nowLine = (
    <div className="day-now">
      <span className="day-now__time">
        <span className="visually-hidden">Now, </span>
        {minutesToTime(now.minute)}
      </span>
    </div>
  )

  return (
    <div className="day-view">
      <p className="calendar-summary">
        {plan.count === 0
          ? 'No appointments'
          : `${plan.count} ${plan.count === 1 ? 'appointment' : 'appointments'} · ${formatPrice(plan.bookedPence)}`}
      </p>
      {plan.timeOff && <div className="day-banner">{plan.timeOff.name ?? 'Time off'} · you’re away</div>}
      {plan.closed && (
        <div className="day-banner">
          Closed · no working hours on this day
          {/* A week with no hours at all is a new account: point at the fix (P6). */}
          {windows.length === 0 && (
            <button type="button" className="btn btn-quiet btn-compact" onClick={() => dispatch({ type: 'OPEN_SETTINGS_PANEL', payload: 'hours' })}>
              Set your working hours <Icon name="chevron-right" size={16} />
            </button>
          )}
        </div>
      )}

      <div className="day-rows">
        {plan.rows.map((row, i) => (
          <React.Fragment key={row.kind === 'booking' ? row.appointment.id : row.kind === 'timeBlock' ? row.block.id : `${row.kind}-${row.start}`}>
            {i === nowAt && nowLine}
            <div className="day-row">
              <div className="day-row__time">
                {minutesToTime(row.start)}
                {row.kind === 'booking' && <small>{minutesToTime(row.appointment.end_minute)}</small>}
              </div>
              {row.kind === 'booking' && <BookingCard appointment={row.appointment} cardShown={cardShown} />}
              {row.kind === 'free' && (
                <button
                  type="button"
                  className="day-free"
                  onClick={() =>
                    dispatch({
                      type: 'OPEN_TAP_CHOICE',
                      payload: { date, startMinute: row.start, endMinute: blockDefaultEnd(date, row.start, windows, appointments, timeBlocks) },
                    })
                  }
                >
                  <span className="day-free__title">
                    Free {minutesToTime(row.start)}–{minutesToTime(row.end)}
                  </span>
                  <span className="day-free__note">{formatDuration(row.end - row.start)} · tap to book or block</span>
                </button>
              )}
              {row.kind === 'timeBlock' && (
                <button
                  type="button"
                  className="day-timeblock"
                  onClick={() => dispatch({ type: 'OPEN_BLOCK_SHEET', payload: { date, startMinute: row.start, endMinute: row.end, block: row.block } })}
                >
                  <span className="day-timeblock__title">{row.block.label ?? 'Blocked'}</span>
                  <span className="day-timeblock__note">
                    {minutesToTime(row.start)}–{minutesToTime(row.end % 1440)} · {formatDuration(row.end - row.start)}
                  </span>
                </button>
              )}
              {row.kind === 'break' && <div className="day-break">Break · {formatDuration(row.end - row.start)}</div>}
              {row.kind === 'blocked' && (
                <div className="day-blocked">Blocked until {minutesToTime(row.end % 1440)} · {formatDuration(row.end - row.start)}</div>
              )}
            </div>
          </React.Fragment>
        ))}
        {nowAt === -1 && nowLine}
      </div>
    </div>
  )
}

function BookingCard({ appointment: a, cardShown }: { appointment: Appointment; cardShown: boolean }) {
  const { state, dispatch } = useApp()
  return (
    <AppointmentRow
      appointment={a}
      timezone={state.me?.professional.timezone}
      cardShown={cardShown}
      className={a.awaiting_deposit ? 'day-booking day-booking--held' : 'day-booking'}
      onOpen={() => dispatch({ type: 'OPEN_APPOINTMENT', payload: a.id })}
    />
  )
}
